import { useCallback, useEffect, useState } from 'react';
import { FiMoreVertical } from 'react-icons/fi';
import styles from './Points.module.css';
import StatCard from '../../components/StatCard/StatCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import Modal, { ModalTitle } from '../../components/Modal/Modal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import CoinIcon from '../../icons/CoinIcon';
import LimitCard from '../../components/LimitCard/LimitCard';
import { api, mapPackage, mapPurchase, mapSettings, money } from '../../lib/api';

const EMPTY_FORM = { name: '', points: '1', price: '', popular: false };

const EMPTY_BONUS_FORM = { enabled: true, points: '125', maxRecipients: '2000' };

const Points = () => {
    const [stats, setStats] = useState([]);
    const [packages, setPackages] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [packageSearch, setPackageSearch] = useState('');
    const [purchaseSearch, setPurchaseSearch] = useState('');
    const [form, setForm] = useState(EMPTY_FORM);
    const [addOpen, setAddOpen] = useState(false);
    const [editPackage, setEditPackage] = useState(null);
    const [removePackage, setRemovePackage] = useState(null);
    const [settings, setSettings] = useState(null);
    const [bonusForm, setBonusForm] = useState(EMPTY_BONUS_FORM);
    const [bonusOpen, setBonusOpen] = useState(false);

    const loadData = useCallback(async () => {
        try {
            const [statsRes, pkgRes, purRes, settingsRes] = await Promise.all([
                api('/admin/stats'),
                api('/admin/points/packages'),
                api('/admin/points/purchases'),
                api('/admin/settings'),
            ]);
            setStats([
                { value: String(statsRes.pointsSold ?? 0), label: 'Points sold' },
                { value: money(statsRes.pointsRevenue ?? 0), label: 'Revenue from points' },
                { value: String(statsRes.pointsInCirculation ?? 0), label: 'Points in circulation' },
            ]);
            setPackages(pkgRes.packages.map(mapPackage));
            setPurchases(purRes.purchases.map(mapPurchase));
            setSettings(mapSettings(settingsRes.settings));
        } catch {
            // leave existing state
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const filteredPackages = packages.filter((pkg) =>
        pkg.name.toLowerCase().includes(packageSearch.toLowerCase())
    );

    const filteredPurchases = purchases.filter((purchase) => {
        const query = purchaseSearch.toLowerCase();
        return (
            purchase.seller.toLowerCase().includes(query) ||
            purchase.package.toLowerCase().includes(query)
        );
    });

    const openAddModal = () => {
        setForm(EMPTY_FORM);
        setAddOpen(true);
    };

    const openEditModal = (pkg) => {
        setForm({
            name: pkg.name,
            points: pkg.points === null ? '' : String(pkg.points),
            price: pkg.price === '--' ? '' : pkg.price.replace(/[^0-9.]/g, ''),
            popular: pkg.recommended,
        });
        setEditPackage(pkg);
    };

    const formValid = form.name.trim() && form.price.trim() && Number(form.points) > 0;

    const handleAdd = async () => {
        try {
            await api('/admin/points/packages', {
                method: 'POST',
                body: {
                    name: form.name.trim(),
                    price: Number(form.price.replace(/[^0-9.]/g, '').trim()),
                    points: Number(form.points),
                    recommended: form.popular,
                },
            });
            await loadData();
        } catch (err) {
            alert(err.message);
        }
        setAddOpen(false);
    };

    const handleEdit = async () => {
        try {
            await api(`/admin/points/packages/${editPackage._id}`, {
                method: 'PATCH',
                body: {
                    name: form.name.trim(),
                    price: Number(form.price.replace(/[^0-9.]/g, '').trim()),
                    points: Number(form.points),
                    recommended: form.popular,
                },
            });
            await loadData();
        } catch (err) {
            alert(err.message);
        }
        setEditPackage(null);
    };

    const handleMarkRecommended = async (pkg) => {
        try {
            await api(`/admin/points/packages/${pkg._id}`, { method: 'PATCH', body: { recommended: true } });
            await loadData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleRemove = async () => {
        try {
            await api(`/admin/points/packages/${removePackage._id}`, { method: 'DELETE' });
            await loadData();
        } catch (err) {
            alert(err.message);
        }
        setRemovePackage(null);
    };

    const openBonusModal = () => {
        setBonusForm({
            enabled: settings.signupBonus.enabled,
            points: String(settings.signupBonus.points),
            maxRecipients: String(settings.signupBonus.maxRecipients),
        });
        setBonusOpen(true);
    };

    // 0 is a valid entry in both fields — an unlimited cap, or a bonus of
    // nothing — so these are bounds checks, not truthiness checks.
    const bonusValid =
        Number.isInteger(Number(bonusForm.points)) &&
        Number(bonusForm.points) >= 0 &&
        Number.isInteger(Number(bonusForm.maxRecipients)) &&
        Number(bonusForm.maxRecipients) >= 0;

    const handleSaveBonus = async () => {
        try {
            await api('/admin/settings', {
                method: 'PATCH',
                body: {
                    signupBonus: {
                        enabled: bonusForm.enabled,
                        points: Number(bonusForm.points),
                        maxRecipients: Number(bonusForm.maxRecipients),
                    },
                },
            });
            await loadData();
        } catch (err) {
            alert(err.message);
        }
        setBonusOpen(false);
    };

    const renderPackageForm = (title, submitLabel, onSubmit, onClose) => (
        <Modal onClose={onClose}>
            <ModalTitle>{title}</ModalTitle>

            <label htmlFor="pkg-name" className={`${styles.inputLabel} mt-4 mb-2`}>Package name</label>
            <input
                id="pkg-name"
                type="text"
                className={`${styles.input} w-100`}
                placeholder="E.g. Mega"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
            />

            <label htmlFor="pkg-points" className={`${styles.inputLabel} mt-4 mb-2`}>Points</label>
            <input
                id="pkg-points"
                type="number"
                min="1"
                className={`${styles.input} w-100`}
                value={form.points}
                onChange={(event) => setForm({ ...form, points: event.target.value })}
            />

            <label htmlFor="pkg-price" className={`${styles.inputLabel} mt-4 mb-2`}>Price</label>
            <input
                id="pkg-price"
                type="text"
                className={`${styles.input} w-100`}
                placeholder="K 0.00"
                value={form.price}
                onChange={(event) => setForm({ ...form, price: event.target.value })}
            />

            <label className={`${styles.checkboxRow} d-flex align-items-center gap-2 mt-4`}>
                <input
                    type="checkbox"
                    checked={form.popular}
                    onChange={(event) => setForm({ ...form, popular: event.target.checked })}
                />
                Mark as popular
            </label>

            <Button className="w-100 mt-4" disabled={!formValid} onClick={onSubmit}>
                {submitLabel}
            </Button>
        </Modal>
    );

    return (
        <div>
            <div className="row g-3">
                {stats.map(({ value, label }) => (
                    <div key={label} className="col-12 col-md-4">
                        <StatCard value={value} label={label} />
                    </div>
                ))}
            </div>

            {settings && (
                <div className="row g-3 mt-0">
                    <div className="col-12 col-lg-6">
                        <LimitCard
                            title="Welcome bonus"
                            description={`${settings.signupBonus.points} free points on signup`}
                            used={settings.signupBonus.awarded}
                            limit={settings.signupBonus.maxRecipients}
                            usedNoun="accounts"
                            status={settings.signupBonus.available ? 'Active' : 'Inactive'}
                            statusVariant={settings.signupBonus.available ? undefined : 'muted'}
                            actionLabel="Edit welcome bonus"
                            onAction={openBonusModal}
                            footnote="One per phone number, for life. Closing an account and signing up again does not earn it a second time."
                        />
                    </div>
                </div>
            )}

            <div className={`${styles.card} mt-4 p-4`}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h2 className={`${styles.cardTitle} mb-0`}>Point packages</h2>

                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <SearchInput
                            placeholder="Search for packages..."
                            value={packageSearch}
                            onChange={(event) => setPackageSearch(event.target.value)}
                        />
                        <Button className={styles.addButton} onClick={openAddModal}>
                            Add package
                        </Button>
                    </div>
                </div>

                <div className={`${styles.tableWrap} table-responsive mt-3`}>
                    <table className={`${styles.table} w-100`}>
                        <thead>
                            <tr>
                                <th>Seller</th>
                                <th>Price</th>
                                <th>Points</th>
                                <th>Purchases</th>
                                <th>Date</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPackages.map((pkg) => (
                                <tr key={pkg.id}>
                                    <td>
                                        <span className="d-inline-flex align-items-center gap-2">
                                            {pkg.name}
                                            {pkg.recommended && <StatusBadge status="Recommended" />}
                                        </span>
                                    </td>
                                    <td className={styles.price}>{pkg.price}</td>
                                    <td>
                                        <span className={`${styles.points} d-inline-flex align-items-center gap-2`}>
                                            <CoinIcon size={16} />
                                            {pkg.points === null ? '--' : `${pkg.points} points`}
                                        </span>
                                    </td>
                                    <td>{pkg.purchases}</td>
                                    <td>{pkg.date}</td>
                                    <td className="text-end position-relative">
                                        <ActionMenu
                                            ariaLabel="Package actions"
                                            items={[
                                                { label: 'Edit package', onClick: () => openEditModal(pkg) },
                                                { label: 'Mark as recommended', onClick: () => handleMarkRecommended(pkg) },
                                                { label: 'Remove package', danger: true, onClick: () => setRemovePackage(pkg) },
                                            ]}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={`${styles.card} mt-4 p-4`}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h2 className={`${styles.cardTitle} mb-0`}>Point purchase</h2>

                    <SearchInput
                        placeholder="Search for purchases..."
                        value={purchaseSearch}
                        onChange={(event) => setPurchaseSearch(event.target.value)}
                    />
                </div>

                <div className={`${styles.tableWrap} table-responsive mt-3`}>
                    <table className={`${styles.table} w-100`}>
                        <thead>
                            <tr>
                                <th>Seller</th>
                                <th>Package</th>
                                <th>Paid</th>
                                <th>Points</th>
                                <th>Payment method</th>
                                <th>Date</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPurchases.map((purchase) => (
                                <tr key={purchase.id}>
                                    <td className={styles.seller}>{purchase.seller}</td>
                                    <td>{purchase.package}</td>
                                    <td className={styles.price}>{purchase.paid}</td>
                                    <td>
                                        <span className={`${styles.points} d-inline-flex align-items-center gap-2`}>
                                            <CoinIcon size={16} />
                                            {purchase.points} points
                                        </span>
                                    </td>
                                    <td>{purchase.method}</td>
                                    <td>{purchase.date}</td>
                                    <td className="text-end">
                                        <button type="button" className={styles.kebabButton} aria-label="Purchase actions">
                                            <FiMoreVertical size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {bonusOpen && (
                <Modal onClose={() => setBonusOpen(false)}>
                    <ModalTitle>Welcome bonus</ModalTitle>

                    <label htmlFor="bonus-points" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Points awarded on signup
                    </label>
                    <input
                        id="bonus-points"
                        type="number"
                        min="0"
                        className={`${styles.input} w-100`}
                        value={bonusForm.points}
                        onChange={(event) => setBonusForm({ ...bonusForm, points: event.target.value })}
                    />

                    <label htmlFor="bonus-max" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Maximum accounts that can receive it
                    </label>
                    <input
                        id="bonus-max"
                        type="number"
                        min="0"
                        className={`${styles.input} w-100`}
                        value={bonusForm.maxRecipients}
                        onChange={(event) => setBonusForm({ ...bonusForm, maxRecipients: event.target.value })}
                    />
                    <p className={`${styles.helpText} mt-2 mb-0`}>
                        {settings.signupBonus.awarded.toLocaleString()} already awarded. Enter 0 for no
                        limit. Setting this below the number already awarded closes the offer — it never
                        takes points back from anyone.
                    </p>

                    <label className={`${styles.checkboxRow} d-flex align-items-center gap-2 mt-4`}>
                        <input
                            type="checkbox"
                            checked={bonusForm.enabled}
                            onChange={(event) => setBonusForm({ ...bonusForm, enabled: event.target.checked })}
                        />
                        Offer the welcome bonus to new signups
                    </label>

                    <Button className="w-100 mt-4" disabled={!bonusValid} onClick={handleSaveBonus}>
                        Save changes
                    </Button>
                </Modal>
            )}

            {addOpen && renderPackageForm('Add new points package', 'Add package', handleAdd, () => setAddOpen(false))}

            {editPackage && renderPackageForm('Edit points package', 'Save package', handleEdit, () => setEditPackage(null))}

            {removePackage && (
                <ConfirmModal
                    title="Remove package"
                    confirmLabel="Remove package"
                    onConfirm={handleRemove}
                    onClose={() => setRemovePackage(null)}
                />
            )}
        </div>
    );
};

export default Points;
